import { IAddressAttributes } from '../domain/entities/Address';
import SHIPPING_METHODS from './ShippingMethods';

const SHIPPING_ADDRESS = 'shipping';
const BILLING_ADDRESS = 'billing';
const COMPANY_BILLING_DETAILS = 'companyBilling';
const PAYMENT = 'payment';
const SHIPPING_TO_PAYMENT = 'shippingToPayment';
const GIFT_CARD = 'GiftCard';
const HTTP = 'http';
const UPS_VALIDATION = 'upsValidation';
const APO = 'APO';
const DPO = 'DPO';
const FPO = 'FPO';
const PHONE_PREFIXES = Object.freeze({
  CL: '56',
  CO: '57',
  MX: '52',
  NA: '1',
  TR: '90',
});
const NO_DATE_CART_ITEM = 'standard';

export const US_PHONE_PREFIX = '+1 ';
export const US_REGION_ID = 0;

export const TYPEAHEAD_SEARCH_TRIGGER_LENGTH = 4;

export enum INPUT_TYPE {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  SELECT = 'select',
  TEL = 'tel',
  TYPEAHEAD = 'typeahead',
}

export enum STORE_TYPE {
  LOCAL_COLLECTION = 0,
  STORE_COLLECTION = 1,
}

export enum ADDRESS_TYPE {
  SHIPPING = 'shipping',
  BILLING = 'billing',
  EDIT = 'edit',
  PACKSTATION = 'packstation',
  COMPANY_BILLING = 'companyBilling',
}

export enum ADDRESS_TYPE_ID {
  SHIPPING = 1,
  BILLING = 2,
  SWEEPSTACK_CAMPAIGN_PROMOTION = 3,
  PACKSTATION = 4,
  BOPIS = 9,
}

export enum PRIVACY_POLICY {
  REJECTED = 0,
  ACCEPTED = 1,
}

export enum DeliveryOptions {
  HOME_DELIVERY = 'home-delivery',
  CLICK_AND_COLLECT = 'click-and-collect',
  SAME_DAY_DELIVERY = 'courier', //TODO RCO-10766 can we update to 'same-day-delivery'
  PACKSTATION = 'packstation',
  CONVENIENCE_STORE_PICK_UP = 'convenience-store-pick-up',
  UPS_STORE_PICK_UP = 'ups-store-pick-up',
  AU_POST_COLLECTIONS = 'au-post-collections',
  ELECTRONIC_DELIVERY = 'electronic-delivery',
}

// VARCHAR2(1) in database
export enum INVOICE_DONATE_MARKS {
  NONE = '',
  ELECTRONIC_INVOICE = '0',
  DONATION = '1',
  PAPER_INVOICE = '2',
}

export enum INVOICE_CARRIER_TYPES {
  NONE = '',
  MEMBERSHIP = 'CTSTK',
  // mobile barcode
  EGUI = '3J0002',
}

export enum TIMEOUT_INTERVAL {
  NULL = 0,
  TEN = 10,
  FIFTY = 50,
  HUNDRED = 100,
  SIX_HUNDRED = 600,
  ONE_TWO_HUNDRED = 1200,
}

export enum UPS_MODAL_OPTIONS {
  KEEP_ADDRESS = 'keep',
  USE_RECOMMENDED = 'recommended',
}

export enum UPS_INDICATORS {
  NO_CANDIDATES = 'NoCandidatesIndicator',
  AMBIGUOUS = 'AmbiguousAddressIndicator',
}

export const SHIPPING_TO_DELIVERY_OPTIONS_MAPPING: Partial<Record<SHIPPING_METHODS, DeliveryOptions>> = {
  [SHIPPING_METHODS.NONE]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.STANDARD]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.BOPIS]: DeliveryOptions.CLICK_AND_COLLECT,
  [SHIPPING_METHODS.COURIER]: DeliveryOptions.SAME_DAY_DELIVERY,
  [SHIPPING_METHODS.NAMED_DAY]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.SECOND_DAY]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.SECOND_DAY_PM]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.OVERNIGHT]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.STANDARD2]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.OVERNIGHT2]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.SATURDAY]: DeliveryOptions.HOME_DELIVERY,
  [SHIPPING_METHODS.ELECTRONIC_SHIPPING]: DeliveryOptions.ELECTRONIC_DELIVERY,
  [SHIPPING_METHODS.LOCAL_COLLECTION]: DeliveryOptions.UPS_STORE_PICK_UP,
  [SHIPPING_METHODS.STORE_COLLECTION]: DeliveryOptions.UPS_STORE_PICK_UP,
};

export enum FIELD_TYPE {
  ADDRESS_SEARCH = 'address_search',
  ADDRESS_TYPE = 'address_type', // eslint-disable-line @typescript-eslint/no-shadow
  BAD_FIELDS = 'bad_fields',
  BILL_TO_COMPANY = 'bill_to_company',
  CITY = 'city',
  COLONY = 'colony',
  COMPANY_ADDRESS = 'company_address',
  COMPANY_NAME = 'company_name',
  COMPANY_TAX_NUMBER = 'company_tax_number',
  CORPORATE_NAME = 'corporate_name',
  CORPORATE_TAX_ID = 'corporate_tax_id',
  COUNTRY = 'country',
  COUNTRY_ID = 'country_id',
  COUNTRY_LABEL = 'country_label',
  CREATED_DATE = 'created_date',
  DEFAULT_SHIPPING = 'default_shipping',
  DELIVERY_INSTRUCTIONS = 'delivery_instructions',
  DISTRICT = 'district',
  EMAIL = 'email',
  FIFTH_LINE_ADDRESS = 'address5',
  FIRST_LINE_ADDRESS = 'address1',
  FIRST_NAME = 'first_name',
  FIRST_NAME_ALTERNATE = 'first_name_alternate',
  FOURTH_LINE_ADDRESS = 'address4',
  FULL = 'full',
  FULL_NAME = 'full_name',
  ID = 'address_id',
  IDENTIFICATION_TYPE = 'identification_type',
  INVOICE_ADDRESS = 'invoice_address',
  INVOICE_CARRIER_TYPE = 'invoice_carrier_type',
  INVOICE_CARRIER_ID = 'invoice_carrier_id',
  INVOICE_DONATE_MARK = 'invoice_donate_mark',
  INVOICE_DONATION_LOVE_CODE = 'invoice_donation_love_code',
  INVOICE_DONATION_NAME = 'invoice_donation_name',
  INVOICE_WITH_SHIPPING = 'invoice_with_shipping',
  LAST_NAME = 'last_name',
  LAST_NAME_ALTERNATE = 'last_name_alternate',
  MUNICIPALITY = 'municipality',
  MOBILE_PHONE = 'phone',
  PHONE = 'phone1',
  PHONE1_TYPE = 'phone1_type',
  PHONE2_TYPE = 'phone2_type',
  SECOND_LINE_ADDRESS = 'address2',
  SECOND_PHONE = 'phone2',
  SHIP_TYPE = 'ship_type',
  STATE = 'state',
  STYLIST_NAME = 'stylist_name',
  THIRD_LINE_ADDRESS = 'address3',
  UPDATED_DATE = 'updated_date',
  ZIPCODE = 'postal_code',
}

export const NAVIGATION_ITEM_IDS = {
  deliveryId: 'pp_progress_bar_delivery',
  paymentId: 'dp_progress_bar_payment',
};

export const DEFAULT_AUTO_FILL_ADDRESS_FIELDS: Array<keyof IAddressAttributes> = [FIELD_TYPE.CITY, FIELD_TYPE.STATE];
export const DEFAULT_ADDRESS_SUGGESTION_FIELDS = DEFAULT_AUTO_FILL_ADDRESS_FIELDS;

export const AU_POST_LOCATIONS_WIDGET_ID = 'au-post-collections';

export enum BACKEND_VALIDATION_TYPES {
  AUPost = 'aupost',
  UPS = 'ups',
}

export const NCCC_PAYMENT_ID = {
  INSTALLMENTS_PREFIX: 'installments_',
  NORMAL: 'normal',
  REDEMPTION: 'redemption',
};

export default {
  APO,
  BACKEND_VALIDATION_TYPES,
  BILLING_ADDRESS,
  COMPANY_BILLING_DETAILS,
  DPO,
  FPO,
  GIFT_CARD,
  HTTP,
  NAVIGATION_ITEM_IDS,
  NCCC_PAYMENT_ID,
  NO_DATE_CART_ITEM,
  PAYMENT,
  PHONE_PREFIXES,
  PRIVACY_POLICY,
  SHIPPING_ADDRESS,
  SHIPPING_TO_PAYMENT,
  UPS_VALIDATION,
};
